import { DocumentsType, EmploymentTypes, IOnboardingType } from 'types';
import { IPreferableLanguages } from '../userApplications/type';

export interface IUserStore {
  userInfo: IUserInfo;
  userPayments: IUserPayments;
  phoneLogin: string | null;
  refreshToken?: string | null;
  verifyDocumentInfo: IVerifyDocumentInfo | null;
  verifyIFCS: any;
  experian: IExperian | null;
  verifyBankAccount: IVerifyBankAccount | null;
  isSendOTPExperianLoader: boolean;
  aadhaarCardInfo: IAadhaarCardInfo | null;
  isSelfieUploaded: boolean;
  isPanCardUploaded: boolean;
  statusOfLoan: any;
  isConsentGivenSent: boolean;
  kyc_details: IKycDetails | null;
  isShowLoader: boolean;
  isForensicsRequestProcessed: boolean;
  isForensicsRequestProcessing: boolean;
  isShowActionLoader: boolean;
  forensics_details: IForensicsDetails | null;
  livenessResult: ILivenessResult | null;
  isSendAadhaarOtpLoading: boolean;
  appSetting: IAppSettings | null;
  retryAfter?: number;
  preferable_languages: IPreferableLanguages[] | null;
  aadhaarVerificationInfo: {
    token: string | null;
    expiredTime: string | null;
    sessionClientId: string | null;
    status?: string;
  };
}

export enum AadhaarCardRequestStatus {
  UNKNOWN = 'unknown',
  PENDING = 'pending',
  PROCESSED = 'processed',
  ERROR = 'error',
  FAILURE = 'failure',
  SUCCESS = 'success',
}

export enum PaymentState {
  PROCESSED = 'processed',
  PENDING = 'pending',
  CANCELLED = 'cancelled',
}

export enum ExperianFlowStepState {
  ERROR = 'error',
  INITIATED = 'initiated',
  OTP_GENERATED = 'otp_generated',
  OTP_VALIDATE_FAILED = 'otp_validate_failed',
  MASKED_MOBILE_INITIATED = 'masked_mobile_initiated',
  MASKED_MOBILE_OTP_GENERATED = 'masked_mobile_otp_generated',
  MASKED_MOBILE_OTP_VALIDATE_FAILED = 'masked_mobile_otp_validate_failed',
  CRQ_PROCESSING = 'crq_processing',
  UNKNOWN = 'unknown',
  FINISHED = 'finished',
  PROCESSED = 'processed',
  PROCESSING = 'processing',
  EXPIRED = 'expired',
  PENDING = 'pending',
}

export interface IAadhaarCardInfo {
  isLoading: boolean;

  status: AadhaarCardRequestStatus;
  otp: string;
  gender: string;
  address: string;
  dob: Date;
  locality: string;
  street: string;
  house: string;
  post_office: string;
  district: string;
  state: string;
  postal_code: string;
  pin_code: string;
}

export interface IVerifyDocumentInfo {
  first_name: null | string;
  last_name: null | string;
  request_id: number;
  request_state: null | VerifyDocumentRequestState;
  request_error: string;
  address: null | string;
  birthday: null | string;
  district: null | string;
  error_code: string;
  gender: null | string;
  pin_code: null | string;
  state: null | string;
  transaction_id: string;
}

export enum VerifyDocumentRequestState {
  Pending = 'pending',
  PROCESSED = 'processed',
  ERROR = 'error',
}

export enum AadhaarCardStatus {
  Pending = 'pending',
  SUCCESS = 'success',
  ERROR = 'error',
  FAILURE = 'failure',
}

export enum VerifyBankAccountState {
  PENDING = 'pending',
  SUCCESS = 'success',
  ERROR = 'error',
  FAILURE = 'failure',
  PROCESSED = 'processed',
  POSTPONED = 'postponed',
}

export enum VerifyIFCS_State {
  PENDING = 'pending',
  SUCCESS = 'success',
  ERROR = 'error',
  FAILURE = 'failure',
  PROCESSED = 'processed',
}

export enum PanCardRequestState {
  Pending = 'pending',
  PROCESSED = 'processed',
  ERROR = 'error',
}

export enum UserState {
  CREATED = 'created',
  CONFIRMED = 'confirmed',
  ACTIVE = 'active',
  BLOCKED = 'blocked',
}

export interface ICreditBureau {
  current_experian_mode: string;
  kyc_request_id: number;
  kyc_request_engine: string;
  kyc_request_state: string;
  is_kyc_request_finished: boolean;
  flow_step: null | string;
  credit_score: null | string;
  error_message: string;
  pdf_link: string;
}

export interface IBankStatementAnalysis {
  current_bank_statement_analysis_mode: null | string;
  consent_url: null | string;
  kyc_request_id: null | number;
  kyc_request_engine: null | string;
  kyc_request_state: null | string;
  is_kyc_request_finished: null | string;
  error_message: null | string;
}

export interface IKycDetails {
  credit_bureau: ICreditBureau;
  bank_statement_analysis: IBankStatementAnalysis;
}
export interface IForensicsDetails {
  access_token: string;
  transaction_id: string;
}
export interface ILivenessResult {
  isLoading: boolean;
}

export interface IUserInfo {
  id: number;
  isLoading: boolean;
  isLoaded: boolean;
  isFailed: boolean;
  username?: string;
  registration_step: number;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  state?: UserState;
  is_data_processing_consent_given?: boolean;
  is_credit_bureau_consent_given?: boolean;
  is_residence_pin_code_in_serviceable_area?: boolean;
  is_registration_pin_code_in_serviceable_area?: boolean;
  phone_number?: string;
  pan_card_number?: string;
  id_number?: string;
  moratorium_till?: string;

  is_in_black_list?: boolean;
  education?: number | null;

  employment_type?: EmploymentTypes | null;
  monthly_income?: string;
  marital_status?: number | null;

  number_of_children?: string;
  email?: string;
  contact_name?: string;
  contact_phone_number?: string;
  contact_relationship?: string;
  employment_colleague_name?: string;
  employment_colleague_phone_number?: string;
  otp?: string;

  aadhaar_number?: string;
  gender?: string | null;
  birthday?: string | Date | null;

  registration_district?: string | null;
  registration_pin_code?: string | null;
  registration_flat?: string | null;
  registration_house?: string | null;
  registration_street?: string | null;
  registration_locality?: string | null;
  registration_post_office?: string | null;
  registration_state_code?: string | null;
  registration_address?: string | null;

  document_number?: string;
  bank_name?: string;
  bank_account_number?: string;
  ifsc_code?: string;
  uploaded_files?: DocumentsType;
  is_approved_photo?: boolean;
  onboarding?: IOnboardingType;

  sold_loan_buyer_message?: string;
  block_type?: string;
}

export interface IUserPayments {
  paymentHistory: IPayment[];

  isLoading: boolean;
  isLoaded: boolean;
  isFailed: boolean;
}

export interface IPayment {
  received_at: string;
  amount: number;
  loan_id: number;
  processed_at: string | null;
  state: PaymentState;
}

export interface IExperian {
  request_id: number;
  request_state: ExperianFlowStepState;
  flow_step: string;
  error_string: string;
  isLoading: boolean;
}

export interface IEngineData {
  action: null | string;
  ifsc_code: null | string;
  account_number: null | string;
  verification_request_id: null | string;
}

export interface IVerifyBankAccount {
  state: null | VerifyBankAccountState;
  isLoading: boolean;
  bank_status: null | string;
  engine_data: null | IEngineData;
}

export interface IConsentGiven {
  isLoading: boolean;
}

export interface IVerifyIFCS {
  state: null | VerifyBankAccountState;
  isLoading: boolean;
  data: any;
}

export interface IAppSettings {
  external: {
    google_tag_manager: {
      isEnabled: boolean;
      params: {};
    };
    chat_widget: {
      is_enabled: boolean;
      params: {
        widget_id: string;
        whatsapp_phone: string;
      };
    };
    aadhaar_verification: {
      engine: string;
      engine_title: string;
    };
  };
  application: {
    allowed_client_age: {
      max: number;
      min: number;
    };
  };
}
